import { useEffect, useRef, useState } from "react";
import { Filter, X } from "lucide-react";
import {
  COMPARE_LABEL,
  CONDITION_LABEL,
  columnFilterLabel,
  type ColumnFilter,
  type ColumnFilterMap,
} from "@pkhoffice/shared";
import { cn } from "@/lib/utils";

/**
 * Ô lọc trên đầu cột — dựng lại hàng `auxhead` của lưới hệ cũ.
 *
 * Hệ cũ: biểu tượng phễu bên trái, ô nhãn bên phải; phễu chuyển đỏ và ô nhãn
 * hiện tóm tắt khi bộ lọc đang bật. Cột chữ gõ thẳng rồi Enter, ba kiểu còn lại
 * mở hộp chọn có nút "Lọc dữ liệu" / "Bỏ lọc".
 */
export type ColumnFilterKind = "text" | "list" | "date" | "number";

export type FacetOption = { value: string | number | null; label: string };

const boxClass =
  "h-6 w-full min-w-0 rounded border border-border bg-background px-1.5 text-xxs text-foreground " +
  "placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary";

function useOutsideClose(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, close]);
  return ref;
}

function PopupShell({
  children,
  onApply,
  onClear,
  onClose,
}: {
  children: React.ReactNode;
  onApply: () => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const ref = useOutsideClose(true, onClose);
  return (
    <div
      ref={ref}
      className="absolute left-0 top-full z-30 mt-1 w-64 rounded-md border border-border bg-background p-2 shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-1.5 normal-case">{children}</div>
      <div className="mt-2 flex gap-1.5">
        <button
          type="button"
          className="flex-1 rounded bg-primary px-2 py-1 text-xxs font-medium text-primary-foreground hover:opacity-90"
          onClick={onApply}
        >
          Lọc dữ liệu
        </button>
        <button
          type="button"
          className="flex-1 rounded border border-border px-2 py-1 text-xxs hover:bg-muted"
          onClick={onClear}
        >
          Bỏ lọc
        </button>
      </div>
    </div>
  );
}

function ListPopup({
  options,
  isLoading,
  selected,
  onApply,
  onClear,
  onClose,
}: {
  options: FacetOption[];
  isLoading?: boolean;
  selected: (string | number | null)[];
  onApply: (values: (string | number | null)[]) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(selected);
  const shown = options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (v: string | number | null) =>
    setPicked((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  return (
    <PopupShell onApply={() => onApply(picked)} onClear={onClear} onClose={onClose}>
      <input
        className={boxClass}
        placeholder="Tìm trong danh sách…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="max-h-52 overflow-y-auto rounded border border-border">
        {isLoading && <div className="p-2 text-xxs text-muted-foreground">Đang tải…</div>}
        {!isLoading && shown.length === 0 && (
          <div className="p-2 text-xxs text-muted-foreground">Không có giá trị</div>
        )}
        {shown.map((o) => (
          <label
            key={String(o.value)}
            className="flex cursor-pointer items-center gap-1.5 px-1.5 py-1 text-xxs hover:bg-muted"
          >
            <input
              type="checkbox"
              className="h-3 w-3"
              checked={picked.includes(o.value)}
              onChange={() => toggle(o.value)}
            />
            <span className="truncate" title={o.label}>
              {o.label}
            </span>
          </label>
        ))}
      </div>
    </PopupShell>
  );
}

function DatePopup({
  value,
  onApply,
  onClear,
  onClose,
}: {
  value?: Extract<ColumnFilter, { kind: "date" }>;
  onApply: (v: Extract<ColumnFilter, { kind: "date" }>) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<Extract<ColumnFilter, { kind: "date" }>>(
    value ?? { kind: "date", compareFrom: 2, condition: 1, compareTo: 5 },
  );
  const compare = (key: "compareFrom" | "compareTo"): React.ReactNode => (
    <select
      className={boxClass}
      value={v[key]}
      onChange={(e) => setV((s) => ({ ...s, [key]: Number(e.target.value) as 1 | 2 | 3 | 4 | 5 }))}
    >
      {Object.entries(COMPARE_LABEL).map(([code, label]) => (
        <option key={code} value={code}>
          {label}
        </option>
      ))}
    </select>
  );
  return (
    <PopupShell onApply={() => onApply(v)} onClear={onClear} onClose={onClose}>
      {compare("compareFrom")}
      <input
        type="date"
        className={boxClass}
        value={v.dateFrom ?? ""}
        onChange={(e) => setV((s) => ({ ...s, dateFrom: e.target.value || undefined }))}
      />
      <select
        className={cn(boxClass, "w-1/2")}
        value={v.condition}
        onChange={(e) => setV((s) => ({ ...s, condition: Number(e.target.value) as 1 | 2 }))}
      >
        {Object.entries(CONDITION_LABEL).map(([code, label]) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
      {compare("compareTo")}
      <input
        type="date"
        className={boxClass}
        value={v.dateTo ?? ""}
        onChange={(e) => setV((s) => ({ ...s, dateTo: e.target.value || undefined }))}
      />
    </PopupShell>
  );
}

function NumberPopup({
  value,
  onApply,
  onClear,
  onClose,
}: {
  value?: Extract<ColumnFilter, { kind: "number" }>;
  onApply: (v: Extract<ColumnFilter, { kind: "number" }>) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<Extract<ColumnFilter, { kind: "number" }>>(
    value ?? { kind: "number" },
  );
  const set = (key: "numberFrom" | "numberTo") => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV((s) => ({ ...s, [key]: e.target.value === "" ? undefined : Number(e.target.value) }));
  return (
    <PopupShell onApply={() => onApply(v)} onClear={onClear} onClose={onClose}>
      <input
        type="number"
        className={boxClass}
        placeholder="Từ"
        value={v.numberFrom ?? ""}
        onChange={set("numberFrom")}
      />
      <input
        type="number"
        className={boxClass}
        placeholder="Đến"
        value={v.numberTo ?? ""}
        onChange={set("numberTo")}
      />
    </PopupShell>
  );
}

export function ColumnFilterCell({
  kind,
  value,
  onChange,
  facets,
  facetsLoading,
  onOpenFacets,
}: {
  kind: ColumnFilterKind;
  value?: ColumnFilter;
  onChange: (v: ColumnFilter | undefined) => void;
  facets?: FacetOption[];
  facetsLoading?: boolean;
  onOpenFacets?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value?.kind === "text" ? value.value : "");
  const active = value !== undefined;

  useEffect(() => {
    if (value?.kind === "text") setDraft(value.value);
    if (value === undefined) setDraft("");
  }, [value]);

  const openPopup = () => {
    if (kind === "list") onOpenFacets?.();
    setOpen(true);
  };

  return (
    <div className="relative flex items-center gap-1">
      <button
        type="button"
        aria-label="Lọc theo cột"
        title={active ? columnFilterLabel(value) : "Lọc theo cột"}
        className={cn(
          "shrink-0 rounded p-0.5 hover:bg-muted",
          active ? "text-danger" : "text-muted-foreground",
        )}
        onClick={kind === "text" ? () => onChange(undefined) : openPopup}
      >
        {kind === "text" && active ? <X className="h-3 w-3" /> : <Filter className="h-3 w-3" />}
      </button>

      {kind === "text" ? (
        <input
          className={cn(boxClass, "normal-case", active && "border-danger")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onChange(draft.trim() ? { kind: "text", value: draft.trim() } : undefined)}
          onKeyDown={(e) => {
            if (e.key === "Enter")
              onChange(draft.trim() ? { kind: "text", value: draft.trim() } : undefined);
          }}
        />
      ) : (
        <input
          readOnly
          onClick={openPopup}
          value={active ? columnFilterLabel(value) : ""}
          title={active ? columnFilterLabel(value) : undefined}
          className={cn(boxClass, "cursor-pointer normal-case", active && "border-danger")}
        />
      )}

      {open && kind === "list" && (
        <ListPopup
          options={facets ?? []}
          isLoading={facetsLoading}
          selected={value?.kind === "list" ? value.values : []}
          onApply={(values) => {
            onChange(values.length ? { kind: "list", values } : undefined);
            setOpen(false);
          }}
          onClear={() => {
            onChange(undefined);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
      {open && kind === "date" && (
        <DatePopup
          value={value?.kind === "date" ? value : undefined}
          onApply={(v) => {
            onChange(v.dateFrom || v.dateTo ? v : undefined);
            setOpen(false);
          }}
          onClear={() => {
            onChange(undefined);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
      {open && kind === "number" && (
        <NumberPopup
          value={value?.kind === "number" ? value : undefined}
          onApply={(v) => {
            onChange(v.numberFrom !== undefined || v.numberTo !== undefined ? v : undefined);
            setOpen(false);
          }}
          onClear={() => {
            onChange(undefined);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/** Trạng thái bộ lọc từng cột + nút "Bỏ tất cả bộ lọc" của hệ cũ. */
export function useColumnFilters(onChanged?: () => void) {
  const [filters, setFilters] = useState<ColumnFilterMap>({});
  const set = (key: string, v: ColumnFilter | undefined) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (v === undefined) delete next[key];
      else next[key] = v;
      return next;
    });
    onChanged?.();
  };
  const clearAll = () => {
    setFilters({});
    onChanged?.();
  };
  return { filters, set, clearAll, count: Object.keys(filters).length };
}
